import {Children, ClassComponent, Vnode} from 'mithril';
import app from 'flarum/forum/app';
import Dropdown from 'flarum/common/components/Dropdown';
import Button from 'flarum/common/components/Button';
import Separator from 'flarum/common/components/Separator';
import Group from 'flarum/common/models/Group';
import Badge from 'flarum/common/components/Badge';
import GroupBadge from 'flarum/common/components/GroupBadge';

const translationPrefix = 'kilowhat-formulaire.forum.permission-controls.';

function badgeForId(id: string) {
    const group = app.store.getById('groups', id);

    return group ? GroupBadge.component({group, label: null}) : '';
}

interface PermissionDropdownAttrs {
    className?: string
    buttonClassName?: string
    label?: Children
    groupIds: string[]
    disallowInherit?: boolean
    allowGuest?: boolean
    guestMeansDisabled?: boolean
    onchange: (groupIds: string[] | null) => void
}

export default class PermissionDropdown extends Dropdown implements ClassComponent<PermissionDropdownAttrs> {
    attrs!: PermissionDropdownAttrs

    static initAttrs(attrs: PermissionDropdownAttrs) {
        super.initAttrs(attrs);

        attrs.className = 'PermissionDropdown';
        attrs.buttonClassName = 'Button Button--text';
    }

    view(vnode: Vnode<PermissionDropdownAttrs, this>) {
        const children: Children = [];

        let groupIds = this.attrs.groupIds;

        const inherit = groupIds === null;
        const everyone = groupIds && groupIds.indexOf(Group.GUEST_ID) !== -1;
        const members = groupIds && groupIds.indexOf(Group.MEMBER_ID) !== -1;
        const adminGroup = app.store.getById<Group>('groups', Group.ADMINISTRATOR_ID)!;

        if (inherit) {
            this.attrs.label = Badge.component({icon: 'fas fa-angle-double-down'});
        } else if (everyone) {
            this.attrs.label = Badge.component({icon: 'fas fa-globe'});
        } else if (members) {
            this.attrs.label = Badge.component({icon: 'fas fa-user'});
        } else {
            this.attrs.label = [badgeForId(Group.ADMINISTRATOR_ID), groupIds.map(badgeForId)];
        }

        if (this.showing) {
            if (!this.attrs.disallowInherit) {
                children.push(
                    m(Button, {
                        icon: inherit ? 'fas fa-check' : true,
                        onclick: () => this.save(null),
                    }, [
                        Badge.component({icon: 'fas fa-angle-double-down'}),
                        ' ',
                        app.translator.trans(translationPrefix + 'inherit'),
                    ])
                );
            }

            if (this.attrs.allowGuest) {
                children.push(
                    m(Button, {
                        icon: everyone ? 'fas fa-check' : true,
                        onclick: () => this.save([Group.GUEST_ID]),
                        disabled: this.isGroupDisabled(Group.GUEST_ID),
                    }, [
                        Badge.component({icon: 'fas fa-globe'}),
                        ' ',
                        app.translator.trans(translationPrefix + (this.attrs.guestMeansDisabled ? 'disabled_members_button' : 'everyone_button')),
                    ])
                );
            }

            children.push(
                m(Button, {
                    icon: members ? 'fas fa-check' : true,
                    onclick: () => this.save([Group.MEMBER_ID]),
                    disabled: this.isGroupDisabled(Group.MEMBER_ID),
                }, [
                    Badge.component({icon: 'fas fa-user'}),
                    ' ',
                    app.translator.trans(translationPrefix + 'members_button'),
                ]),

                Separator.component(),

                m(Button, {
                    icon: !inherit && !everyone && !members ? 'fas fa-check' : true,
                    disabled: !inherit && !everyone && !members,
                    onclick: (e: KeyboardEvent) => {
                        if (e.shiftKey) e.stopPropagation();
                        this.save([]);
                    },
                }, [
                    badgeForId(adminGroup.id()!),
                    ' ',
                    adminGroup.namePlural(),
                ])
            );

            ([] as Children[]).push.apply(
                children,
                (app.store.all('groups') as Group[])
                    .filter((group) => [Group.ADMINISTRATOR_ID, Group.GUEST_ID, Group.MEMBER_ID].indexOf(group.id()!) === -1)
                    .map((group) =>
                        m(Button, {
                            icon: groupIds && groupIds.indexOf(group.id()!) !== -1 ? 'fas fa-check' : true,
                            onclick: (e: KeyboardEvent) => {
                                if (e.shiftKey) e.stopPropagation();
                                this.toggle(group.id()!);
                            },
                            disabled: this.isGroupDisabled(group.id()!) && this.isGroupDisabled(Group.MEMBER_ID) && this.isGroupDisabled(Group.GUEST_ID),
                        }, [badgeForId(group.id()!), ' ', group.namePlural()])
                    )
            );
        }

        // @ts-ignore Flarum doesn't think a non-abstract parent method exists
        return super.view({...vnode, children});
    }

    save(groupIds: string[] | null) {
        this.attrs.onchange(groupIds);
    }

    toggle(groupId: string) {
        let groupIds = this.attrs.groupIds || [];

        const index = groupIds.indexOf(groupId);

        if (index !== -1) {
            groupIds.splice(index, 1);
        } else {
            groupIds.push(groupId);
            groupIds = groupIds.filter((id) => [Group.GUEST_ID, Group.MEMBER_ID].indexOf(id) === -1);
        }

        this.save(groupIds);
    }

    isGroupDisabled(id: string) {
        // Not used for now, but let's keep it so the code stays close to core
        return false;
    }
}
